/**
 * Referral API Client
 *
 * Handles referral/invite system including:
 * - Checking invite code validity
 * - Binding user to inviter
 * - Fetching invite stats and rewards
 */

import config from '@/config';

const BASE_URL = config.API_BASE_URL;

// Error codes from backend (MessageCode enum)
export const BackendErrorCodes = {
  INVALID_REQUEST: 1000,
  CANNOT_INVITE_YOURSELF: 30000,
  INVALID_INVITE_CODE: 30001,
  CANNOT_USE_INVITEE_INVITE_CODE: 30002,
  USER_ALREADY_HAS_INVITE_CODE: 30003,
  USER_INVITE_INFO_NOT_FOUND: 30004,
  USER_NOT_FOUND: 30005,
  FAILED_TO_UPDATE_REBATE_RATE: 30006,
  NO_REWARDS_TO_CLAIM: 30007,
} as const;

export enum ReferralErrorCode {
  INVALID_INVITE_CODE = 'INVALID_INVITE_CODE',
  CANNOT_INVITE_YOURSELF = 'CANNOT_INVITE_YOURSELF',
  USER_ALREADY_HAS_INVITE_CODE = 'USER_ALREADY_HAS_INVITE_CODE',
  CANNOT_USE_INVITEE_INVITE_CODE = 'CANNOT_USE_INVITEE_INVITE_CODE',
  INVALID_REQUEST = 'INVALID_REQUEST',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  NO_REWARDS_TO_CLAIM = 'NO_REWARDS_TO_CLAIM',
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  NETWORK_ERROR = 'NETWORK_ERROR',
}

/**
 * Map backend error code numbers to string error codes
 */
function mapBackendErrorCode(errorCode: number): string {
  switch (errorCode) {
    case BackendErrorCodes.INVALID_REQUEST:
      return ReferralErrorCode.INVALID_REQUEST;
    case BackendErrorCodes.CANNOT_INVITE_YOURSELF:
      return ReferralErrorCode.CANNOT_INVITE_YOURSELF;
    case BackendErrorCodes.INVALID_INVITE_CODE:
      return ReferralErrorCode.INVALID_INVITE_CODE;
    case BackendErrorCodes.CANNOT_USE_INVITEE_INVITE_CODE:
      return ReferralErrorCode.CANNOT_USE_INVITEE_INVITE_CODE;
    case BackendErrorCodes.USER_ALREADY_HAS_INVITE_CODE:
      return ReferralErrorCode.USER_ALREADY_HAS_INVITE_CODE;
    case BackendErrorCodes.USER_INVITE_INFO_NOT_FOUND:
    case BackendErrorCodes.USER_NOT_FOUND:
      return ReferralErrorCode.USER_NOT_FOUND;
    case BackendErrorCodes.NO_REWARDS_TO_CLAIM:
      return ReferralErrorCode.NO_REWARDS_TO_CLAIM;
    default:
      return ReferralErrorCode.INVALID_REQUEST;
  }
}

// Custom error class for referral operations
export class ReferralError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'ReferralError';
  }
}

export interface CheckInviteCodeRequest {
  invite_code: string;
}

export interface CheckInviteCodeResponse {
  code: number;
  data: {
    is_valid: boolean;
  };
  message?: string;
}

export interface BindInviteCodeRequest {
  invite_code: string;
}

export interface BindInviteCodeResponse {
  code: number;
  message?: string;
  error?: string;
}

export interface InviteInfo {
  invite_code: string; // User's own invite code
  invite_by: string; // Inviter's invite code (empty if no inviter)
  invite_l1_count: number; // Number of direct invites
}

export interface GetInviteInfoResponse {
  code: number;
  data: InviteInfo;
  message?: string;
}

export interface NextTier {
  tier_key: string;
  chest_threshold: number;
  chests_remaining: number;
  reward_points: number;
  reward_sol: number;
}

export interface DashboardData {
  self_point: string; // User's own points from opening chests
  invitee_point: string; // Points from invitees
  total_point: string; // Total points (self + invitee)
  invite_code: string;
  invited_by_code: string;
  total_invitees: number;
  total_invitee_chests: number;
  claimable_points: number;
  claimable_sol: number;
  total_claimed_points: number;
  total_claimed_sol: number;
  next_tier: NextTier | null;
}

export interface GetDashboardResponse {
  code: number;
  data: DashboardData;
  message?: string;
}

export interface ClaimRewardsResponse {
  code: number;
  data: {
    points_claimed: number;
    sol_claimed: number;
  };
  message?: string;
}

/**
 * Check if an invite code is valid
 *
 * @param inviteCode - 5-character invite code
 * @returns Whether the code is valid
 * @throws ReferralError if the request fails
 */
export async function checkInviteCode(inviteCode: string): Promise<CheckInviteCodeResponse> {
  try {
    const response = await fetch(`${BASE_URL}/invite/check-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ invite_code: inviteCode.toUpperCase() }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ReferralError(
        data.error || ReferralErrorCode.INVALID_REQUEST,
        data.message || `Failed to check invite code: ${response.statusText}`,
        response.status
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ReferralError) {
      throw error;
    }
    throw new ReferralError(
      ReferralErrorCode.INVALID_REQUEST,
      error instanceof Error ? error.message : 'Failed to check invite code'
    );
  }
}

/**
 * Bind current user to an inviter using their invite code
 * Can only be done once per user
 * Uses JWT token from HTTP-only cookie
 *
 * @param inviteCode - Inviter's invite code
 * @returns Success response
 * @throws ReferralError if the request fails
 */
export async function bindInviteCode(
  inviteCode: string
): Promise<BindInviteCodeResponse> {
  try {
    const response = await fetch(`${BASE_URL}/invite/bind-inviter-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ invite_code: inviteCode.toUpperCase() }),
    });

    const data = await response.json();

    // Check for authentication errors
    if (response.status === 401 || response.status === 403) {
      throw new ReferralError(
        ReferralErrorCode.AUTHENTICATION_REQUIRED,
        'Authentication required. Please sign in with your wallet.',
        response.status
      );
    }

    // Backend returns {code: number, message: string, data?: any}
    // code: 0 = success, non-zero = error
    if (data.code !== 0) {
      const errorCode = mapBackendErrorCode(data.code);
      const message = data.message || getErrorMessage(errorCode, inviteCode);
      
      throw new ReferralError(
        errorCode,
        message,
        response.status
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ReferralError) {
      throw error;
    }
    throw new ReferralError(
      ReferralErrorCode.NETWORK_ERROR,
      error instanceof Error ? error.message : 'Failed to bind invite code'
    );
  }
}

/**
 * Get a detailed error message based on error code
 */
function getErrorMessage(errorCode: string, inviteCode?: string): string {
  switch (errorCode) {
    case ReferralErrorCode.INVALID_INVITE_CODE:
      return inviteCode 
        ? `Referral code "${inviteCode}" not found or has expired.`
        : 'The referral code you entered is invalid or does not exist.';
    case ReferralErrorCode.CANNOT_INVITE_YOURSELF:
      return 'You cannot use your own referral code.';
    case ReferralErrorCode.USER_ALREADY_HAS_INVITE_CODE:
      return 'You are already bound to a referral network.';
    case ReferralErrorCode.CANNOT_USE_INVITEE_INVITE_CODE:
      return 'You cannot use a code from someone you invited.';
    case ReferralErrorCode.AUTHENTICATION_REQUIRED:
      return 'Please sign in with your wallet to bind a referral code.';
    case ReferralErrorCode.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again.';
    case ReferralErrorCode.USER_NOT_FOUND:
      return 'User account not found.';
    default:
      return 'Failed to bind invite code. Please try again.';
  }
}

/**
 * Get user's invite information
 * Includes their own invite code, who invited them, and invite count
 * Uses JWT token from HTTP-only cookie
 *
 * @returns User's invite info
 */
export async function getInviteInfo(): Promise<GetInviteInfoResponse> {
  const response = await fetch(`${BASE_URL}/invite/info`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get invite info: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get user's referral dashboard data
 * Includes invite stats, claimable rewards, and tier info
 * Uses JWT token from HTTP-only cookie
 *
 * @returns Dashboard data
 */
export async function getDashboard(): Promise<GetDashboardResponse> {
  const response = await fetch(`${BASE_URL}/invite/dashboard`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to get dashboard: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Claim available referral rewards
 * Uses JWT token from HTTP-only cookie
 *
 * @returns Claimed rewards info
 */
export async function claimRewards(): Promise<ClaimRewardsResponse> {
  const response = await fetch(`${BASE_URL}/invite/claim-rewards`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to claim rewards: ${response.statusText}`);
  }

  return response.json();
}

export interface AccessCheckResponse {
  code: number;
  data: {
    can_access: boolean;
    has_inviter: boolean;
  };
  message?: string;
}

export async function checkAccess(): Promise<AccessCheckResponse> {
  const response = await fetch(`${BASE_URL}/invite/access-check`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to check access: ${response.statusText}`);
  }

  return response.json();
}
